import PropTypes from 'prop-types';
import React from 'react';
import _ from 'lodash';
import { connect } from 'react-redux';
import { Field, useFormState } from 'react-final-form';
import {
  InputField,
  CheckBoxField,
  SimpleSelectField,
} from '@unite-us/app-components';
import '../stylesheets/contactInformation.scss';

// Validation functions
const isEmail = (value) => {
  if (!value) return undefined;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(value) ? undefined : 'Invalid email address';
};

const isPhoneNumber = (value) => {
  if (!value) return undefined;
  // Remove formatting characters and check for valid length
  const digits = value.replace(/\D/g, '');
  return digits.length >= 10 ? undefined : 'Invalid phone number';
};

// Sub-component for Email Section
function EmailSection({ communicationTypes }) {
  return (
    <div className="row">
      <div className="col-xs-6 contact-information__email">
        <Field name="email.email_address" validate={isEmail}>
          {({ input, meta }) => (
            <InputField
              {...input}
              label="Email"
              id="Email"
              type="email"
              error={meta.touched ? meta.error : undefined}
            />
          )}
        </Field>
      </div>
      <div className="col-xs-12 contact-information__phone">
        <div className="communication-types-checkboxes">
          {communicationTypes.written.map((type, index) => (
            <div
              className="communication-types-checkboxes__checkbox-container"
              key={`email-${type.value}`}
            >
              <Field name={`email.acceptable_communication_types.${type.value}`} type="checkbox">
                {({ input }) => (
                  <CheckBoxField
                    className="communication-types-checkboxes__checkbox"
                    id={`email-communication-types-${index}-${type.value}-checkbox`}
                    label={type.display_name}
                    checked={input.checked}
                    onChange={input.onChange}
                  />
                )}
              </Field>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

EmailSection.propTypes = {
  communicationTypes: PropTypes.shape({
    oral: PropTypes.array,
    written: PropTypes.array,
  }).isRequired,
};

// Sub-component for Phone Section
function PhoneSection({ phoneTypes, communicationTypes }) {
  const formState = useFormState();
  const phoneType = _.get(formState.values, 'phone.phone_type', '');

  // Determine which communication types to show based on phone type
  const canReceiveOral = ['mobile', 'work', 'home'].includes(phoneType);
  const canReceiveWritten = ['mobile'].includes(phoneType);

  const visibleCommunicationTypes = [
    ...(canReceiveOral ? communicationTypes.oral : []),
    ...(canReceiveWritten ? communicationTypes.written : []),
  ];

  return (
    <div className="row">
      <div className="col-xs-12 col-sm-6 col-md-4">
        <Field name="phone.phone_number" validate={isPhoneNumber}>
          {({ input, meta }) => (
            <InputField
              {...input}
              className="phone-number-field"
              label="Phone Number"
              id="phone-number-field"
              mask="(999) 999-9999 x 99999"
              placeholder="(999) 999-9999"
              error={meta.touched ? meta.error : undefined}
            />
          )}
        </Field>
      </div>

      <div className="col-xs-12 col-sm-3 contact-information__phone-type">
        <Field name="phone.phone_type">
          {({ input, meta }) => (
            <SimpleSelectField
              className="phone-type-field"
              id="phone-type-field"
              label="Phone Type"
              value={input.value}
              onChange={input.onChange}
              onBlur={input.onBlur}
              options={phoneTypes}
              optionSettings={{ labelKey: 'display_name', valueKey: 'value' }}
              error={meta.touched ? meta.error : undefined}
              menuPortalTarget={document.body}
            />
          )}
        </Field>
      </div>
      <div className="col-xs-12">
        <div className="communication-types-checkboxes">
          {visibleCommunicationTypes.map((type, index) => (
            <div
              className="communication-types-checkboxes__checkbox-container"
              key={`phone-${type.value}`}
            >
              <Field name={`phone.acceptable_communication_types.${type.value}`} type="checkbox">
                {({ input }) => (
                  <CheckBoxField
                    className="communication-types-checkboxes__checkbox"
                    id={`phone-communication-types-${index}-${type.value}-checkbox`}
                    label={type.display_name}
                    checked={input.checked}
                    onChange={input.onChange}
                  />
                )}
              </Field>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

PhoneSection.propTypes = {
  phoneTypes: PropTypes.array.isRequired,
  communicationTypes: PropTypes.shape({
    oral: PropTypes.array,
    written: PropTypes.array,
  }).isRequired,
};

// Sub-component for Contact Preferences
function ContactPreferencesSection({ contactMethodOptions, timeOfDayOptions }) {
  return (
    <>
      <div className="row">
        <div className="col-xs-12 col-md-6">
          <Field name="preferred_communication_method">
            {({ input, meta }) => (
              <SimpleSelectField
                className="preferred-method-of-contact-field"
                id="preferred-method-of-contact-field"
                label="Methods of Contact"
                value={input.value}
                onChange={input.onChange}
                onBlur={input.onBlur}
                options={contactMethodOptions}
                optionSettings={{ labelKey: 'display_name', valueKey: 'value' }}
                error={meta.touched ? meta.error : undefined}
                menuPortalTarget={document.body}
              />
            )}
          </Field>
        </div>
      </div>
      <div className="row">
        <div className="col-xs-12 col-md-6">
          <Field name="preferred_communication_time_of_day">
            {({ input, meta }) => (
              <SimpleSelectField
                className="best-time-to-contact-field"
                id="best-time-to-contact-field"
                label="Best Time To Contact"
                value={input.value}
                onChange={input.onChange}
                onBlur={input.onBlur}
                options={timeOfDayOptions}
                optionSettings={{ labelKey: 'display_name', valueKey: 'value' }}
                error={meta.touched ? meta.error : undefined}
                menuPortalTarget={document.body}
              />
            )}
          </Field>
        </div>
      </div>
    </>
  );
}

ContactPreferencesSection.propTypes = {
  contactMethodOptions: PropTypes.array.isRequired,
  timeOfDayOptions: PropTypes.array.isRequired,
};

// Sub-component for Address Fields
function AddressSection({ addressTypes, usStates }) {
  const formState = useFormState();
  const addresses = _.get(formState.values, 'addresses', [{}]);

  return (
    <>
      {addresses.map((address, index) => (
        <div id={`address-${index}`} key={index} className="address-section">
          <h6 className="address-section__title">Address {index + 1}</h6>
          <div className="row">
            <div className="col-xs-12">
              <Field name={`addresses[${index}].address_type`}>
                {({ input, meta }) => (
                  <SimpleSelectField
                    id={`address-${index}-type`}
                    label="Address Type"
                    value={input.value}
                    onChange={input.onChange}
                    onBlur={input.onBlur}
                    options={addressTypes}
                    optionSettings={{ labelKey: 'display_name', valueKey: 'value' }}
                    error={meta.touched ? meta.error : undefined}
                    menuPortalTarget={document.body}
                  />
                )}
              </Field>
            </div>
          </div>
          <div className="row">
            <div className="col-xs-12">
              <Field name={`addresses[${index}].line_1`}>
                {({ input, meta }) => (
                  <InputField
                    {...input}
                    id={`address-${index}-line1`}
                    label="Street Address"
                    error={meta.touched ? meta.error : undefined}
                  />
                )}
              </Field>
            </div>
          </div>
          <div className="row">
            <div className="col-xs-12">
              <Field name={`addresses[${index}].line_2`}>
                {({ input, meta }) => (
                  <InputField
                    {...input}
                    id={`address-${index}-line2`}
                    label="Apt, Suite, etc."
                    error={meta.touched ? meta.error : undefined}
                  />
                )}
              </Field>
            </div>
          </div>
          <div className="row">
            <div className="col-xs-12 col-sm-6">
              <Field name={`addresses[${index}].city`}>
                {({ input, meta }) => (
                  <InputField
                    {...input}
                    id={`address-${index}-city`}
                    label="City"
                    error={meta.touched ? meta.error : undefined}
                  />
                )}
              </Field>
            </div>
            <div className="col-xs-12 col-sm-3">
              <Field name={`addresses[${index}].state`}>
                {({ input, meta }) => (
                  <SimpleSelectField
                    id={`address-${index}-state`}
                    label="State"
                    value={input.value}
                    onChange={input.onChange}
                    onBlur={input.onBlur}
                    options={usStates}
                    optionSettings={{ labelKey: 'display_name', valueKey: 'value' }}
                    error={meta.touched ? meta.error : undefined}
                    menuPortalTarget={document.body}
                  />
                )}
              </Field>
            </div>
            <div className="col-xs-12 col-sm-3">
              <Field name={`addresses[${index}].postal_code`}>
                {({ input, meta }) => (
                  <InputField
                    {...input}
                    id={`address-${index}-postal-code`}
                    label="Zip Code"
                    error={meta.touched ? meta.error : undefined}
                  />
                )}
              </Field>
            </div>
          </div>
        </div>
      ))}
    </>
  );
}

AddressSection.propTypes = {
  addressTypes: PropTypes.array.isRequired,
  usStates: PropTypes.array.isRequired,
};

function ContactInformation(props) {
  const {
    enums,
    phoneTypes,
    communicationTypes,
  } = props;
  const contactEnums = _.wget(enums, 'contact_data', {});
  const addressTypes = _.get(contactEnums, 'address_types', []);
  const usStates = _.get(enums, 'geography.us_states', []);

  return (
    <div className="contact-information">
      <h3 className="contact-information__title">
        Contact Information
      </h3>

      <hr />

      <EmailSection communicationTypes={communicationTypes} />

      <PhoneSection
        phoneTypes={phoneTypes}
        communicationTypes={communicationTypes}
      />

      <ContactPreferencesSection
        contactMethodOptions={_.get(contactEnums, 'communication_preferences.contact_method', [])}
        timeOfDayOptions={_.get(contactEnums, 'communication_preferences.time_of_day', [])}
      />

      <AddressSection
        addressTypes={addressTypes}
        usStates={usStates}
      />
    </div>
  );
}

ContactInformation.fields = [
  'addresses[].line_1',
  'addresses[].line_2',
  'addresses[].city',
  'addresses[].state',
  'addresses[].postal_code',
  'addresses[].address_type',
  'addresses[].country',
  'preferred_communication_time_of_day',
  'email.email_address',
  'email.acceptable_communication_types.message',
  'email.acceptable_communication_types.notification',
  'phone.acceptable_communication_types.message',
  'phone.acceptable_communication_types.notification',
  'phone.acceptable_communication_types.phone_call',
  'phone.phone_number',
  'phone.phone_type',
  'preferred_communication_method',
  'preferred_communication_time_of_day',
];

ContactInformation.propTypes = {
  enums: PropTypes.object.isRequired,
  phoneTypes: PropTypes.array,
  communicationTypes: PropTypes.shape({
    oral: PropTypes.array,
    written: PropTypes.array,
  }),
};

ContactInformation.defaultProps = {
  phoneTypes: [],
  communicationTypes: {
    oral: [],
    written: [],
  },
};

function mapStateToProps(state) {
  const contactDataEnums = _.wget(state, 'session.enums.contact_data', {});
  return {
    phoneTypes: _.wget(state, 'session.enums.contact_data.phone_types', []),
    communicationTypes: {
      oral: _.wget(contactDataEnums, 'oral_communication_types', []),
      written: _.wget(contactDataEnums, 'written_communication_types', []),
    },
  };
}

export default connect(mapStateToProps)(ContactInformation);
